import { type Flags, useFlag } from '@reflag/react-sdk';
import React from 'react';
import { atomFamily, useRecoilState } from 'recoil';

import { Badge } from '@gitbook/spine-react';
import { useNow } from '@gitbook/util-react-hooks';

import { persistRecoilEffect } from '../../utils';

/**
 * Atom to track when a feature was first seen by a user.
 */
const featuresFirstSeenAtom = atomFamily<number | null, string>({
    default: () => null,
    key: 'featuresFirstSeenAtom',
    effects: [persistRecoilEffect()],
});

type NewFeatureBadgeProps = {
    /**
     * ID of the feature to indicate as new.
     */
    flagKey: keyof Flags;

    /**
     * How long should the badge be displayed for after the user first sees it? In miliseconds.
     * @default 1 week
     */
    showFor?: number;
};

/**
 * Badge to indicate a new feature.
 */
export function NewFeatureBadge(props: NewFeatureBadgeProps) {
    const { flagKey } = props;
    const feature = useFlag(flagKey);

    if (!feature.isEnabled) {
        return null;
    }

    return (
        // We use a suspense boundary as useRecoilState can suspend when the atom is not initialized yet from the storage.
        <React.Suspense fallback={null}>
            <NewFeatureBadgeEnabled {...props} />
        </React.Suspense>
    );
}

function NewFeatureBadgeEnabled(props: NewFeatureBadgeProps) {
    const { flagKey: featureKey, showFor = 1000 * 60 * 60 * 24 * 7 } = props;
    const [firstSeen, setFirstSeen] = useRecoilState(featuresFirstSeenAtom(featureKey));
    const now = useNow();

    const isNew = firstSeen === null || now - firstSeen < showFor;

    // Set the first seen timestamp when the feature is first seen.
    React.useEffect(() => {
        if (firstSeen === null) {
            setFirstSeen(Date.now());
        }
    }, [firstSeen, setFirstSeen]);

    if (!isNew) {
        return null;
    }

    return <Badge kind="beta" size="xsmall" label="New" />;
}
